function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? Object(arguments[i]) : {}; var ownKeys = Object.keys(source); if (typeof Object.getOwnPropertySymbols === 'function') { ownKeys.push.apply(ownKeys, Object.getOwnPropertySymbols(source).filter(function (sym) { return Object.getOwnPropertyDescriptor(source, sym).enumerable; })); } ownKeys.forEach(function (key) { _defineProperty(target, key, source[key]); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import moment from 'moment';
import { createSelector } from 'reselect';
import { totalPriceAfterPromocodeApplied } from 'swag-common/business-logic/payments/total-price-after-promocode-applied';
import { DRAFT_ERRORS } from 'swag-common/interfaces';
import getPriceBasedOnDiscount from 'swag-common/business-logic/payments/get-price-based-on-discount';
import { CREDIT_CARD, CREDITS, DEFAULT_PAYMENT_METHOD } from 'swag-common/constants/payment-methods';
import { isReceiverWithErrors } from 'swag-common/business-logic/custom-store-shipout/is-receiver-with-errors.logic';
import { getProductsFromGroupedContent, groupBundleContentWithSizesByColor } from 'swag-common/utils/custom-store-product/product-is-bundle.util';
import { calculatePickAndPackAmount } from 'swag-common/utils/distributions/calculate-pick-and-pack-amount.service';
import { checkIsShippingRateFlat } from 'swag-common/utils/distributions/check-is-shipping-rate-flat.service';
import { sortPossibleDeliveries } from 'swag-client-common/utils/distributions/shipouts.utils';
import { renameDeliveryOptions } from 'swag-common/utils/rename-delivery-options';
import { capitalizeFirstWord, pluralize } from 'swag-common/utils/strings';
import { popupParamsSelector } from 'swag-client-common/redux/popup-manager/popup-manager.selector';
import { COUNTRY_CODES, DHL_ALLOWED_SHIPPING_OPTIONS } from 'swag-common/constants/shipping.constants';
import checkIsReceiverDomestic from 'swag-common/utils/shipout/check-is-receiver-domestic';
import { CSV_COLUMNS, CSV_CONTACT_COLUMNS, SHIPOUT_STATUSES } from 'swag-common/constants/custom-store-shipouts.constants';
import { mapShipoutCsvRowToHeaderNames } from 'swag-common/utils/shipout/map-shipout-csv-row-to-header-names.service';
import { getQuantityHeaderKeys } from 'swag-common/business-logic/custom-store-shipout/get-quantity-header-keys';
import { mapProductsByName } from 'swag-common/utils/distributions/map-products-by-name.util';
import { getInHandDateFormatted } from 'swag-common/utils/order';
import { INVENTORY_MODES } from '../../utils/constants';
import { storeWarehousePaymentMethodSelector } from '../store-settings/store-settings.selectors';
import { inventoryModeSelector, paymentMethodSelector, userBasedAmountApprovalSelector, userRoleBasedAmountApprovalSelector } from '../inventory/inventory.selectors';
import { promocodeSelector } from '../payment/payment.selectors';
import { getUniqueColorsOfProduct, selectedProductsSelector, selectedProductsSizesAndQuantityPerColorSelector } from '../products/products.selectors';
import { calculateAdvanceDistributionStockInHand, getIsAdvanceDistribution, getIsProductInAdvanceDistribution } from '../../utils/advance-distributions.utils';
import { getDistributionCustomerInHandDate } from '../../utils/shipout.utils';
import { getProductColor, getProductNameFromColorHeader, getProductNameFromSizeHeader, transformVariantsListIntoMap } from './send-to-multiple-addresses.utils';
const sendToMultipleAddressesSelector = state => {
  return state.sendToMultipleAddresses;
};
export const activeStepSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => sendToMultipleAddresses.activeStep);
export const activeStepScreenSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => sendToMultipleAddresses.activeStepScreen);
export const sendToMultipleAddressesIdSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => sendToMultipleAddresses.shipout && sendToMultipleAddresses.shipout._id.toString());
export const isActiveShipoutExists = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => Boolean(sendToMultipleAddresses.shipout));
export const draftIdSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => sendToMultipleAddresses.draftId);
export const validationSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => sendToMultipleAddresses.validation);
export const draftEditorSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => sendToMultipleAddresses.draftEditor);
export const validationProgressValueSelector = createSelector(validationSelector, progress => Math.round(progress.value / progress.total * 100));
export const isValidationProgressCompletedSelector = createSelector(validationSelector, progress => progress.value === progress.total);
export const validationStatusSelector = createSelector(validationSelector, validation => validation.status);
export const isValidationSuccessSelector = createSelector(isValidationProgressCompletedSelector, validationStatusSelector, (isCompleted, status) => isCompleted && status === true);
export const isDraftTableHeaderValidSelector = createSelector(draftEditorSelector, editor => editor.header.valid);
export const isDraftTableBodyValidSelector = createSelector(draftEditorSelector, editor => editor.body.valid);
export const selectedRowInTableSelector = createSelector(draftEditorSelector, editor => editor.body.selectedRow);
export const invalidRowsInTableSelector = createSelector(draftEditorSelector, editor => editor.body.invalidRows);
export const validationSelectedRowInTableSelector = createSelector(selectedRowInTableSelector, number => number === null ? null : number + 1);
export const sendToMultipleAddressesShipoutSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => sendToMultipleAddresses.shipout);
export const shipoutCsvErrorsSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => sendToMultipleAddresses.shipoutCsvErrors);
export const shipoutsFormatErrorsSelector = createSelector(shipoutCsvErrorsSelector, errors => errors.formatErrors);
export const shipoutsInStockErrorsSelector = createSelector(shipoutCsvErrorsSelector, errors => errors.inStockErrors);
export const uploadedCsvUrlSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => sendToMultipleAddresses.uploadedCsvUrl);
export const originalFileNameSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => sendToMultipleAddresses.originalFileName);
export const selectedDomesticShippingMethodSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => {
  return sendToMultipleAddresses.selectedDomesticShippingMethod;
});
export const selectedInternationalShippingMethodSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => sendToMultipleAddresses.selectedInternationalShippingMethod);
export const isDHLInternationalShippingOptionChosenSelector = createSelector(selectedInternationalShippingMethodSelector, selectedMethod => selectedMethod === DHL_ALLOWED_SHIPPING_OPTIONS.PLY);
export const possibleDomesticDeliveriesSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => {
  const deliveries = sortPossibleDeliveries(renameDeliveryOptions(sendToMultipleAddresses.possibleDomesticDeliveries));
  return deliveries.map(delivery => _objectSpread({}, delivery, {
    totalCharges: delivery.shippingPrice
  }));
});
export const possibleInternationalDeliveriesSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => {
  const deliveries = sortPossibleDeliveries(renameDeliveryOptions(sendToMultipleAddresses.possibleInternationalDeliveries));
  return deliveries.map(delivery => _objectSpread({}, delivery, {
    totalCharges: delivery.shippingPrice
  }));
});
export const possibleCSVDeliveriesSelector = createSelector(possibleInternationalDeliveriesSelector, possibleDomesticDeliveriesSelector, (international, domestic) => [...international, ...domestic]);
export const sendToMultipleAddressesShipoutReceiversSelector = createSelector(sendToMultipleAddressesShipoutSelector, shipout => (shipout === null || shipout === void 0 ? void 0 : shipout.receivers) || []);
export const isShipoutHasReceiversSelector = createSelector(sendToMultipleAddressesShipoutReceiversSelector, receivers => Boolean(receivers.length));
export const isShipmentCalculationInProgressSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => sendToMultipleAddresses.isShipmentCalculationInProgress);
export const shippingPriceSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => sendToMultipleAddresses.shippingPrice || 0);
export const pickAndPackPriceSelector = createSelector(sendToMultipleAddressesShipoutReceiversSelector, selectedDomesticShippingMethodSelector, selectedInternationalShippingMethodSelector, (receivers, selectedDomesticShippingMethod, selectedInternationalShippingMethod) => {
  const domesticReceivers = [];
  const internationalReceivers = [];
  receivers.forEach(receiver => {
    if (checkIsReceiverDomestic(receiver)) {
      domesticReceivers.push(receiver);
    } else {
      internationalReceivers.push(receiver);
    }
  });
  const domesticPickAndPack = calculatePickAndPackAmount(domesticReceivers, selectedDomesticShippingMethod);
  const internationalPickAndPack = calculatePickAndPackAmount(internationalReceivers, selectedInternationalShippingMethod);
  return domesticPickAndPack + internationalPickAndPack;
});
export const taxesAllSelector = createSelector(sendToMultipleAddressesShipoutReceiversSelector, receivers => {
  if (!receivers.length) {
    return {
      vat: 0,
      dutyFees: 0,
      fees: 0
    };
  }
  return receivers.reduce((taxSum, {
    shipment
  }) => {
    var _shipment$taxes, _shipment$taxes2, _shipment$taxes3;
    return {
      vat: taxSum.vat + ((shipment === null || shipment === void 0 ? void 0 : (_shipment$taxes = shipment.taxes) === null || _shipment$taxes === void 0 ? void 0 : _shipment$taxes.vat) || 0) || 0,
      dutyFees: taxSum.dutyFees + ((shipment === null || shipment === void 0 ? void 0 : (_shipment$taxes2 = shipment.taxes) === null || _shipment$taxes2 === void 0 ? void 0 : _shipment$taxes2.dutyFees) || 0) || 0,
      fees: taxSum.fees + ((shipment === null || shipment === void 0 ? void 0 : (_shipment$taxes3 = shipment.taxes) === null || _shipment$taxes3 === void 0 ? void 0 : _shipment$taxes3.fees) || 0) || 0
    };
  }, {
    vat: 0,
    dutyFees: 0,
    fees: 0
  });
});
export const taxesAllSumSelector = createSelector(taxesAllSelector, taxes => taxes.vat + taxes.dutyFees + taxes.fees);
export const shouldShowOnlyTaxesLabelSelector = createSelector(taxesAllSelector, allTaxes => {
  return Boolean(allTaxes.vat && !allTaxes.dutyFees && !allTaxes.fees);
});
export const taxesVatSumSelector = createSelector(taxesAllSelector, taxes => taxes.vat);
export const taxesDutyFeesSumSelector = createSelector(taxesAllSelector, taxes => taxes.dutyFees);
export const taxesFeesSumSelector = createSelector(taxesAllSelector, taxes => taxes.fees);
export const shipoutTotalCostSelector = createSelector(shippingPriceSelector, pickAndPackPriceSelector, taxesAllSumSelector, (shippingPrice, pickAndPackPrice, taxesAllSum) => shippingPrice + pickAndPackPrice + taxesAllSum);
export const shipoutPromocodeSelector = createSelector(sendToMultipleAddressesShipoutSelector, promocodeSelector, (shipout, promocode) => (shipout === null || shipout === void 0 ? void 0 : shipout.promocode) || promocode);
export const shipoutTotalCostAfterDiscountSelector = createSelector(shipoutTotalCostSelector, shipoutPromocodeSelector, totalPriceAfterPromocodeApplied);
export const shipoutTotalCostToPaySelector = createSelector(shipoutPromocodeSelector, shipoutTotalCostAfterDiscountSelector, shipoutTotalCostSelector, getPriceBasedOnDiscount);
export const isSMAApprovalFlowSelector = createSelector(userRoleBasedAmountApprovalSelector, userBasedAmountApprovalSelector, shipoutTotalCostToPaySelector, (roleBaseApproval, userBasedApproval, totalSMA) => {
  if (userBasedApproval.isApprovalFlow) {
    return totalSMA >= userBasedApproval.amount;
  }
  if (roleBaseApproval.isApprovalFlow) {
    return totalSMA >= roleBaseApproval.amount;
  }
  return false;
});
export const isShipoutPaidWithCreditCardSelector = createSelector(sendToMultipleAddressesShipoutSelector, shipout => (shipout === null || shipout === void 0 ? void 0 : shipout.paymentMethod) === CREDIT_CARD);
export const isShipoutPaidWithCreditsSelector = createSelector(sendToMultipleAddressesShipoutSelector, shipout => (shipout === null || shipout === void 0 ? void 0 : shipout.paymentMethod) === CREDITS);
export const isShipoutPaidWithCreditsOrCreditCardSelector = createSelector(isShipoutPaidWithCreditsSelector, isShipoutPaidWithCreditCardSelector, (paidWithCredits, paidWithCreditCard) => paidWithCredits || paidWithCreditCard);
export const isShipoutScheduledFlowSelector = createSelector(sendToMultipleAddressesShipoutSelector, shipout => (shipout === null || shipout === void 0 ? void 0 : shipout.status) === SHIPOUT_STATUSES.SCHEDULED);
export const isShipoutApprovalFlowSelector = createSelector(sendToMultipleAddressesShipoutSelector, shipout => (shipout === null || shipout === void 0 ? void 0 : shipout.isApprovalFlow) || false);
export const erroredReceiversArraySelector = createSelector(sendToMultipleAddressesShipoutReceiversSelector, receivers => receivers.map((item, line) => _objectSpread({}, item, {
  line
})).filter(isReceiverWithErrors));
export const erroredReceiversSelector = createSelector(erroredReceiversArraySelector, receivers => receivers.reduce((receiversMap, receiver) => _objectSpread({}, receiversMap, {
  [receiver._id]: receiver
}), {}));
export const erroredReceiversToEditSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => Object.values(sendToMultipleAddresses.erroredReceiversToEdit));
export const erroredReceiversNotValidatedSelector = createSelector(erroredReceiversToEditSelector, erroredReceivers => erroredReceivers.filter(receiver => receiver.isEdited));
export const erroredReceiverAddressSelector = (reduxState, {
  receiverId,
  address: addressPayload
}) => {
  const {
    _id,
    email,
    country,
    city,
    state,
    zip,
    address,
    unit,
    phone,
    isAddressEnteredManually,
    isEdited
  } = reduxState.sendToMultipleAddresses.erroredReceiversToEdit[receiverId];
  const receiverData = {
    _id,
    email,
    country,
    city,
    state,
    zip,
    address,
    unit,
    phone,
    isAddressEnteredManually,
    isEdited
  };
  if (addressPayload) {
    return _objectSpread({}, receiverData, {
      isAddressEnteredManually: true
    }, addressPayload);
  }
  return receiverData;
};
export const shipoutPaymentMethodSelector = createSelector(inventoryModeSelector, storeWarehousePaymentMethodSelector, paymentMethodSelector, (inventoryMode, storeMethod, inventoryMethod) => {
  return inventoryMode === INVENTORY_MODES.STORE ? storeMethod : inventoryMethod;
});
export const isShipoutPaymentMethodDefault = createSelector(shipoutPaymentMethodSelector, shipoutPaymentMethod => shipoutPaymentMethod === DEFAULT_PAYMENT_METHOD);
export const smaShipoutSelectedProductsQuantitiesMapSelector = createSelector(sendToMultipleAddressesShipoutSelector, shipout => {
  if (!(shipout !== null && shipout !== void 0 && shipout.products)) {
    return {};
  }
  return shipout.products.reduce((acc, {
    _id,
    shipoutQuantity
  }) => {
    if (!_id) {
      return acc;
    }
    const productId = String(_id);
    const existingProductQuantity = acc[productId] || 0;
    acc[productId] = existingProductQuantity + shipoutQuantity;
    return acc;
  }, {});
});
export const smaWarehouseQuantitiesSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => sendToMultipleAddresses.warehouseQuantities);
export const getIsSmaWithAdvanceDistributionSelector = createSelector(selectedProductsSelector, smaWarehouseQuantitiesSelector, smaShipoutSelectedProductsQuantitiesMapSelector, (...args) => getIsAdvanceDistribution(...args));
const smaAdvanceDistributionStockInHandSelector = createSelector(selectedProductsSelector, smaWarehouseQuantitiesSelector, smaShipoutSelectedProductsQuantitiesMapSelector, (...args) => calculateAdvanceDistributionStockInHand(...args));
export const smaAdvanceDistributionStockInHandIntervalSelector = createSelector(smaAdvanceDistributionStockInHandSelector, inHandDate => inHandDate && getInHandDateFormatted({
  inHandDate,
  isStrictInHandDate: false
}));
export let SmaAdvanceDistributionTypes = /*#__PURE__*/function (SmaAdvanceDistributionTypes) {
  SmaAdvanceDistributionTypes["inStockOnly"] = "inStockOnly";
  SmaAdvanceDistributionTypes["inProductionOnly"] = "inProductionOnly";
  SmaAdvanceDistributionTypes["stockAndProduction"] = "stockAndProduction";
  return SmaAdvanceDistributionTypes;
}({});
export const smaAdvanceDistributionModeSelector = createSelector(smaWarehouseQuantitiesSelector, smaShipoutSelectedProductsQuantitiesMapSelector, (warehouseQuantities, selectedProducts) => {
  return Object.entries(selectedProducts).reduce((acc, [key, requestedQuantity]) => {
    const warehouseData = warehouseQuantities[key];
    if (!warehouseData || !requestedQuantity) {
      return null;
    }
    let result = null;
    if (acc === SmaAdvanceDistributionTypes.stockAndProduction) {
      return acc;
    }
    if (getIsProductInAdvanceDistribution(requestedQuantity, warehouseData)) {
      result = SmaAdvanceDistributionTypes.inProductionOnly;
    } else {
      result = SmaAdvanceDistributionTypes.inStockOnly;
    }
    if (acc && result !== acc) {
      return SmaAdvanceDistributionTypes.stockAndProduction;
    }
    return result;
  }, null);
});
export const estInHandDateSelector = createSelector(selectedDomesticShippingMethodSelector, possibleDomesticDeliveriesSelector, selectedInternationalShippingMethodSelector, possibleInternationalDeliveriesSelector, getIsSmaWithAdvanceDistributionSelector, smaAdvanceDistributionStockInHandSelector, (selectedDomesticShippingMethod, possibleDomesticDeliveries, selectedInternationalShippingMethod, possibleInternationalDeliveries, isAdvanceDistribution, advanceDistributionStockInHand) => {
  var _domesticDeliveryData, _domesticDeliveryData2;
  const isDomesticShippingFlatRate = checkIsShippingRateFlat({
    serviceCode: selectedDomesticShippingMethod
  });
  const domesticDeliveryData = possibleDomesticDeliveries.find(rate => isDomesticShippingFlatRate ? checkIsShippingRateFlat(rate) : rate.serviceCode === selectedDomesticShippingMethod);
  const isInternationalShippingFlatRate = checkIsShippingRateFlat({
    serviceCode: selectedInternationalShippingMethod
  });
  const internationalDeliveryData = possibleInternationalDeliveries.find(rate => isInternationalShippingFlatRate ? checkIsShippingRateFlat(rate) : rate.serviceCode === selectedInternationalShippingMethod);
  if (!domesticDeliveryData && !internationalDeliveryData) {
    return 'N/A';
  }
  const leastMinDays = domesticDeliveryData !== null && domesticDeliveryData !== void 0 && domesticDeliveryData.minDays && internationalDeliveryData !== null && internationalDeliveryData !== void 0 && internationalDeliveryData.minDays ? Math.min(Number(domesticDeliveryData.minDays), Number(internationalDeliveryData.minDays)) : (_domesticDeliveryData = domesticDeliveryData === null || domesticDeliveryData === void 0 ? void 0 : domesticDeliveryData.minDays) !== null && _domesticDeliveryData !== void 0 ? _domesticDeliveryData : internationalDeliveryData === null || internationalDeliveryData === void 0 ? void 0 : internationalDeliveryData.minDays;
  const biggestMaxDays = domesticDeliveryData !== null && domesticDeliveryData !== void 0 && domesticDeliveryData.maxDays && internationalDeliveryData !== null && internationalDeliveryData !== void 0 && internationalDeliveryData.maxDays ? Math.max(Number(domesticDeliveryData.maxDays), Number(internationalDeliveryData.maxDays)) : (_domesticDeliveryData2 = domesticDeliveryData === null || domesticDeliveryData === void 0 ? void 0 : domesticDeliveryData.maxDays) !== null && _domesticDeliveryData2 !== void 0 ? _domesticDeliveryData2 : internationalDeliveryData === null || internationalDeliveryData === void 0 ? void 0 : internationalDeliveryData.maxDays;
  return getDistributionCustomerInHandDate({
    minDays: String(leastMinDays),
    maxDays: String(biggestMaxDays),
    isAdvanceDistribution,
    advanceDistributionStockInHand
  });
});
export const shipmentCalculationProgressSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => sendToMultipleAddresses.currentShipoutCalculationProgress);
export const shipmentCalculationProgressPercentageSelector = createSelector(shipmentCalculationProgressSelector, ({
  receiversCalculated,
  totalReceivers
}) => {
  if (totalReceivers === 0) {
    return 0;
  }
  return Math.round(receiversCalculated / totalReceivers * 100);
});
export const shipmentCalculationRemainingTimeSelector = createSelector(sendToMultipleAddressesShipoutSelector, shipmentCalculationProgressPercentageSelector, (shipout, progressPercentage) => {
  if (!shipout || !progressPercentage) {
    return null;
  }
  const timeSpent = moment(moment(new Date())).diff(moment(shipout.startedShippingCalculationAt));
  const totalTimeToComplete = timeSpent / progressPercentage * 100;
  const remainingTime = Math.max(totalTimeToComplete - timeSpent, 1000);
  const now = moment();
  const finishMoment = moment(new Date()).add(remainingTime, 'milliseconds');
  return now.to(finishMoment).replace('in a ', '').replace('in ', '');
});
export const numberOfReceiversSelector = createSelector(sendToMultipleAddressesShipoutSelector, shipout => shipout ? shipout.receivers.length : 0);
export const draftHeaderSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => sendToMultipleAddresses.draftHead);
export const initialDraftHeadSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => sendToMultipleAddresses.initialDraftHead);
export const draftHeaderMapSelector = createSelector(draftHeaderSelector, header => {
  return header.reduce((all, item) => {
    const {
      title
    } = item;
    const [name] = title.split('(');
    const matches = title.match(/(\(.*?\).*)/i);
    const hint = matches ? matches[1] : null;
    return _objectSpread({}, all, {
      [item.original]: _objectSpread({}, item, {
        title: capitalizeFirstWord(name),
        hint: hint || ''
      })
    });
  }, {});
});
export const draftReceiversSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => sendToMultipleAddresses.draftReceivers);
export const selectedReceiverIdSelector = createSelector(selectedRowInTableSelector, draftReceiversSelector, (selectedRow, receivers) => receivers[selectedRow || 0]._id);
export const isDraftReceiversWithErrorsSelector = createSelector(draftReceiversSelector, receivers => receivers.some(receiver => receiver.errorsList && Object.keys(receiver.errorsList).length > 0));
export const isDraftReceiversValidSelector = createSelector(isDraftReceiversWithErrorsSelector, selectedRowInTableSelector, invalidRowsInTableSelector, (isWithErrors, selectedRow, invalidRows) => {
  const positionOfInvalidRow = invalidRows.findIndex(position => position === selectedRow);
  return !isWithErrors && positionOfInvalidRow === -1;
});
export const isDraftHeadersWithErrorsSelector = createSelector(draftHeaderSelector, headers => headers.some(header => header.error));
export const columnsWithErrorsSelector = createSelector(draftHeaderSelector, header => header.filter(({
  error
}) => error));
export const isLoqateValidatedSelector = createSelector(sendToMultipleAddressesSelector, sendToMultipleAddresses => sendToMultipleAddresses.isLoqateValidated);
export const draftTitleSelector = createSelector(columnsWithErrorsSelector, shipoutsInStockErrorsSelector, draftReceiversSelector, isDraftTableHeaderValidSelector, invalidRowsInTableSelector, isLoqateValidatedSelector, (columnErrors, shipoutsInStockErrors, receivers, isHeaderValid, invalidRows, isLoqateValidated) => {
  if (columnErrors.length) {
    return {
      text: `Fix your ${pluralize(columnErrors.length, 'column')} ${pluralize(columnErrors.length, 'heading', 's', true)}!`,
      error: true,
      withoutColor: false
    };
  }
  if (invalidRows.length) {
    return {
      text: `You have ${pluralize(invalidRows.length, 'error')} to correct!`,
      error: false,
      withoutColor: true
    };
  }
  if (!isHeaderValid && !columnErrors.length) {
    return {
      text: "You're good to go, verify your CSV!",
      error: false,
      withoutColor: false
    };
  }
  if (isLoqateValidated && shipoutsInStockErrors.length > 0) {
    return {
      text: 'You need additional stock to complete your order!',
      error: true,
      withoutColor: false
    };
  }
  if (isHeaderValid && !invalidRows.length) {
    return {
      text: 'Your file is ready to submit!',
      error: false,
      withoutColor: false
    };
  }
  return {
    text: 'Great, last check before you create distributions!',
    error: false,
    withoutColor: false
  };
});
export const draftReceiversNumberSelector = createSelector(draftReceiversSelector, list => list.length);
export const areAllErrorsFixedSelector = createSelector(columnsWithErrorsSelector, columnErrors => !columnErrors.length);
export const draftTipSelector = createSelector(areAllErrorsFixedSelector, isDraftReceiversWithErrorsSelector, (isErrorsFixed, isReceiversWithErrors) => {
  if (!isErrorsFixed) {
    return 'Edit your heading columns to make sure that they match the content!';
  }
  if (isReceiversWithErrors) {
    return 'Please review and correct the errors outlined in red before continuing.';
  }
  return 'Please finalize your CSV upload to calculate shipping costs!';
});
export const headerOriginalColumnNamesSelector = createSelector(draftHeaderSelector, header => header.map(item => item.original));
export const selectedRowItemFromListSelector = createSelector((state, props) => props.position, draftReceiversSelector, (position, list) => {
  const index = position - 1;
  return list[index > 0 ? index : 0];
});
const bundleContentVariantHasRequiredSizesAndSomeQuantityInStockOrProduction = sizeName => productsArray => {
  const requiredSize = productsArray.filter(({
    sizeSettings,
    sizeIndex: _sizeIndex
  }) => (sizeSettings === null || sizeSettings === void 0 ? void 0 : sizeSettings.options[Number(_sizeIndex)]) === sizeName);
  if (requiredSize.length === 0) {
    return false;
  }
  const {
    inStockQuantity,
    inProductionQuantity
  } = requiredSize[0];
  return inStockQuantity + inProductionQuantity > 0;
};
export const mapProductsSizeHeaderTitlesIntoSizeOptionsSelector = createSelector((state, props) => props.position, draftReceiversSelector, draftHeaderSelector, selectedProductsSizesAndQuantityPerColorSelector, selectedProductsSelector, (position, list, header, products, selectedProducts) => {
  const index = position - 1;
  const row = list[index > 0 ? index : 0];
  const productsMap = {};
  products.forEach(product => {
    const {
      name,
      sizes,
      isApparel,
      isBundle
    } = product;
    if (isBundle) {
      var _bundleSizes$product, _bundleSizes$product$;
      const bundle = selectedProducts.find(_product => _product.name === name);
      const bundleContent = (bundle === null || bundle === void 0 ? void 0 : bundle.variants[0].bundleContent) || [];
      const bundleSizes = bundleContent.find(content => {
        var _content$product, _content$product2, _content$product2$siz;
        return ((_content$product = content.product) === null || _content$product === void 0 ? void 0 : _content$product.sizeSettings) && ((_content$product2 = content.product) === null || _content$product2 === void 0 ? void 0 : (_content$product2$siz = _content$product2.sizeSettings) === null || _content$product2$siz === void 0 ? void 0 : _content$product2$siz.options.length) > 1;
      });
      const sizeOptions = (bundleSizes === null || bundleSizes === void 0 ? void 0 : (_bundleSizes$product = bundleSizes.product) === null || _bundleSizes$product === void 0 ? void 0 : (_bundleSizes$product$ = _bundleSizes$product.sizeSettings) === null || _bundleSizes$product$ === void 0 ? void 0 : _bundleSizes$product$.options) || [];
      if (bundleSizes) {
        const bundleContentWithSizesGroupedByColor = bundleContent.reduce(groupBundleContentWithSizesByColor, {});
        const list = sizeOptions.map(sizeName => {
          const isAvailable = Object.values(bundleContentWithSizesGroupedByColor).map(getProductsFromGroupedContent).every(bundleContentVariantHasRequiredSizesAndSomeQuantityInStockOrProduction(sizeName));
          return {
            _id: sizeName,
            name: isAvailable ? sizeName : `${sizeName} (Out of stock)`,
            isDisabled: !isAvailable
          };
        });
        productsMap[name.toLowerCase()] = list;
      }
    } else {
      const sizeOptions = (sizes === null || sizes === void 0 ? void 0 : sizes.options) || [];
      const {
        stockVariantsMap,
        productionVariantsMap,
        quantities
      } = transformVariantsListIntoMap(product);
      const selectedColor = getProductColor({
        header,
        row,
        isApparel,
        quantities,
        name
      });
      const list = sizeOptions.map((name, sizeIndex) => {
        var _stockVariantsMap$sel, _productionVariantsMa;
        const stockQuantity = ((_stockVariantsMap$sel = stockVariantsMap[selectedColor]) === null || _stockVariantsMap$sel === void 0 ? void 0 : _stockVariantsMap$sel.quantities[sizeIndex]) || 0;
        const productionQuantity = ((_productionVariantsMa = productionVariantsMap[selectedColor]) === null || _productionVariantsMa === void 0 ? void 0 : _productionVariantsMa.quantities[sizeIndex]) || 0;
        const availableQuantity = stockQuantity + productionQuantity;
        const isDisabled = availableQuantity === 0;
        const title = isDisabled ? `${name} (Out of stock)` : name;
        return {
          isDisabled,
          name: title,
          _id: name
        };
      });
      productsMap[name.toLowerCase()] = list;
    }
  });
  return productsMap;
});
export const productColorsSelector = createSelector(selectedProductsSelector, selectedProducts => {
  const productsMap = mapProductsByName(selectedProducts);
  return Object.entries(productsMap).reduce((acc, [name, products]) => {
    const colorsData = getUniqueColorsOfProduct(products[0]);
    const colorsOptions = colorsData.map(({
      colorName,
      inStock,
      inProduction,
      inReserveQuantity
    }) => {
      const availableQuantity = inStock + inProduction - inReserveQuantity;
      const isDisabled = availableQuantity === 0;
      const optionTitle = isDisabled ? `${colorName} (Out of stock)` : colorName;
      return {
        colorName: colorName,
        optionTitle,
        isDisabled
      };
    });
    return _objectSpread({}, acc, {
      [name]: colorsOptions
    });
  }, {});
});
export const selectedRowItemSelector = createSelector(selectedRowItemFromListSelector, draftHeaderSelector, mapProductsSizeHeaderTitlesIntoSizeOptionsSelector, productColorsSelector, (item, header, productsMap, productColorsMap) => {
  return header.reduce((all, {
    original,
    title
  }) => {
    const cellName = title.toLowerCase();
    const isCellWithSizeOption = cellName.includes(' size');
    const cell = {
      name: cellName,
      value: item[original],
      error: '',
      sizes: [],
      colors: []
    };
    if (isCellWithSizeOption) {
      const productName = getProductNameFromSizeHeader(cellName);
      cell.sizes = productsMap[productName] || [];
    }
    const productName = getProductNameFromColorHeader(cellName);
    cell.colors = productColorsMap[productName] || [];
    const {
      errorsList
    } = item;
    const errors = errorsList;
    if (errors) {
      cell.error = errors[cellName] || '';
      if (cellName.endsWith('quantity')) {
        cell.error = errors[DRAFT_ERRORS.INVALID_PRODUCTS_QUANTITY] || '';
      }
    }
    return _objectSpread({}, all, {
      [original]: cell
    });
  }, {});
});
export const selectedRowCountryItemSelector = createSelector(selectedRowItemFromListSelector, draftHeaderSelector, (item, header) => {
  const countryColumn = header.find(item => item.title.toLowerCase() === CSV_COLUMNS.COUNTRY.toLowerCase());
  if (!countryColumn) {
    return '';
  }
  return String(item[countryColumn.original]) || '';
});
export const selectedRowInTableReceiverSelector = createSelector(selectedRowInTableSelector, draftReceiversSelector, (position, list) => {
  if (position === null) {
    return null;
  }
  return list[position];
});
export const selectedRowInTableReceiverIdSelector = createSelector(selectedRowInTableReceiverSelector, receiver => (receiver === null || receiver === void 0 ? void 0 : receiver._id) || '');
export const selectedRowInTableAddressSelector = createSelector(selectedRowInTableReceiverSelector, draftHeaderSelector, (receiver, header) => {
  if (!receiver) {
    return null;
  }
  const row = mapShipoutCsvRowToHeaderNames(receiver, header);
  return {
    address: String(row[CSV_COLUMNS.ADDRESS.toLowerCase()] || ''),
    unit: String(row[CSV_COLUMNS.UNIT.toLowerCase()] || ''),
    country: String(row[CSV_COLUMNS.COUNTRY.toLowerCase()] || ''),
    state: String(row[CSV_COLUMNS.STATE.toLowerCase()] || ''),
    zip: String(row[CSV_COLUMNS.ZIP.toLowerCase()] || ''),
    city: String(row[CSV_COLUMNS.CITY.toLowerCase()] || '')
  };
});
export const validationSelectedRowInTableErrorSelector = createSelector(selectedRowInTableSelector, selectedRowInTableReceiverSelector, draftHeaderSelector, (position, receiver, header) => {
  if (!receiver || position === null) {
    return '';
  }
  const errorsList = receiver.errorsList;
  if (!Object.values(errorsList || {}).length) {
    return '';
  }
  const errors = header.reduce((all, {
    title
  }) => {
    return errorsList[title.toLowerCase()] ? [...all, errorsList[title.toLowerCase()]] : all;
  }, []);
  [DRAFT_ERRORS.INVALID_PRODUCTS_QUANTITY, DRAFT_ERRORS.LOCATION, DRAFT_ERRORS.UPS].forEach(field => {
    if (errorsList[field]) {
      errors.push(errorsList[field]);
    }
  });
  if (!errors[0]) {
    return '';
  }
  const isWithDot = errors[0].endsWith('.');
  return `Line ${position + 1} - ${capitalizeFirstWord(errors[0])}${isWithDot ? '' : '.'}`;
});
export const isDraftReceiverResubmitActiveSelector = createSelector(selectedRowInTableReceiverSelector, receiver => {
  if (!receiver) {
    return true;
  }
  const {
    errorsList
  } = receiver;
  if (!errorsList) {
    return true;
  }
  const errors = errorsList;
  const keys = Object.keys(errors);
  if (!keys.length) {
    return true;
  }
  return Object.keys(errors).every(key => [DRAFT_ERRORS.LOCATION, DRAFT_ERRORS.UPS].includes(key));
});
export const isDraftReceiverDeleteActiveSelector = createSelector(draftReceiversSelector, receivers => {
  return receivers.length > 1;
});
export const selectedRowItemErrorsSelector = createSelector((state, props) => props.position, draftReceiversSelector, (position, list) => {
  const index = position - 1;
  const item = list[index > 0 ? index : 0];
  const errors = item.errorsList || {};
  return {
    list: errors,
    empty: !Object.keys(errors).length
  };
});
export const isUserFixingErrorsInRowSelector = createSelector((state, props) => props.position, selectedRowInTableSelector, (position, selectedRow) => position - 1 === selectedRow);
export const isRowWithErrorsNextToCurrentRowSelector = createSelector(selectedRowInTableSelector, draftReceiversSelector, (state, props) => props.position, (erroredRowPosition, list, currentRowPosition) => {
  if (erroredRowPosition === null) {
    return false;
  }
  return erroredRowPosition + 2 === currentRowPosition;
});
export const isFirstRowInTableSelector = createSelector((state, props) => props.position, position => position === 1);
export const isLastRowInTableSelector = createSelector((state, props) => props.position, draftReceiversNumberSelector, (position, total) => position === total);
export const isTopBorderOfRowMarkedAsErrorSelector = createSelector(isFirstRowInTableSelector, isUserFixingErrorsInRowSelector, isRowWithErrorsNextToCurrentRowSelector, (isFirst, isInvalid, isRowWithErrorsNextToCurrentRow) => isFirst && isInvalid || isRowWithErrorsNextToCurrentRow);
export const itemsForVirtualizedTableSelector = createSelector(draftReceiversNumberSelector, draftHeaderMapSelector, (total, header) => {
  const mock = new Array(total).fill(0);
  return [header, ...mock];
});
export const columnNumberSelector = createSelector(draftHeaderMapSelector, header => Object.keys(header).length);
export const currentColumnSelector = createSelector(popupParamsSelector, draftHeaderSelector, ({
  columnName
}, header) => {
  return header.find(item => item.original === columnName);
});
export const validProductNamesSelector = createSelector(initialDraftHeadSelector, initialHeader => initialHeader.filter(item => !item.isMain));
export const headerNamesListSelector = createSelector(draftHeaderSelector, currentColumnSelector, validProductNamesSelector, (header, column, productNames) => {
  if (!column) {
    return [];
  }
  if (column.isMain) {
    return Object.values(CSV_CONTACT_COLUMNS).reduce((names, value) => {
      const headerColumn = header.find(i => i.title.toLowerCase() === value.toLowerCase());
      const [label] = value.split('(');
      return [...names, {
        label,
        value,
        isSelected: !!headerColumn
      }];
    }, []);
  }
  return productNames.reduce((names, value) => {
    const headerColumn = header.find(i => i.title.toLowerCase() === value.suggestion.toLowerCase());
    return [...names, {
      label: value.suggestion,
      value: value.suggestion,
      isSelected: !!headerColumn
    }];
  }, []);
});
export const headerOriginalNamesOfTitlesSelector = createSelector(draftHeaderSelector, header => {
  return header.reduce((all, column) => {
    return _objectSpread({}, all, {
      [column.title.toLowerCase()]: column.original
    });
  }, {});
});
export const editCurrentColumnSelector = createSelector(currentColumnSelector, column => column || {
  error: true,
  suggestion: '',
  title: 'Select column name'
});
export const textSmaWarningPopupSelector = createSelector(popupParamsSelector, ({
  text
}) => text);
export const isQuantityEditableSelector = createSelector(shipoutsInStockErrorsSelector, isLoqateValidatedSelector, (shipoutsInStockErrors, isLoqateValidated) => shipoutsInStockErrors.length > 0 && isLoqateValidated);
export const quantityHeaderKeysSelector = createSelector(draftHeaderSelector, getQuantityHeaderKeys);
export const newInvalidRowsSelector = createSelector(draftReceiversSelector, receivers => {
  return receivers.reduce((list, {
    errorsList
  }, position) => {
    if (errorsList && Object.keys(errorsList).length) {
      return [...list, position];
    }
    return [...list];
  }, []);
});
export const shouldShowAdditionalTaxesTooltipSMASelector = createSelector(sendToMultipleAddressesShipoutSelector, selectedInternationalShippingMethodSelector, (shipout, shippingMethodInternational) => {
  return Boolean(shipout === null || shipout === void 0 ? void 0 : shipout.receivers.some(receiver => {
    var _receiver$shipment;
    if (receiver.country === COUNTRY_CODES.US) {
      return false;
    }
    const possibleDeliveries = receiver === null || receiver === void 0 ? void 0 : (_receiver$shipment = receiver.shipment) === null || _receiver$shipment === void 0 ? void 0 : _receiver$shipment.possibleDeliveries;
    return !(possibleDeliveries !== null && possibleDeliveries !== void 0 && possibleDeliveries[shippingMethodInternational]);
  }));
});